import {RootState} from '@/redux-store/store';
import {RoomMetadata} from '@/types/matrix';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {PersistConfig, persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export type JoinedRoomData = {
  roomId: string;
  documentId: string;
  metadata: RoomMetadata;
  lastChange: number;
  isDirect: boolean;
  lastMessage: {
    sender: string;
    senderName: string;
    message: string;
    system: boolean;
  } | null;
  unread: number;
};

interface RoomDataSliceState {
  roomsJoined: JoinedRoomData[];
  directChatData: Record<string, string[]>;
}

const initialState: RoomDataSliceState = {
  roomsJoined: [],
  directChatData: {}
};

export const roomDataPersistConfig: PersistConfig<RoomDataSliceState> = {
  key: 'roomData',
  storage
};

const sortByLastChangeDesc = (a: JoinedRoomData, b: JoinedRoomData) => {
  return b.lastChange - a.lastChange;
};

const roomDataSlice = createSlice({
  name: 'roomData',
  initialState,
  reducers: {
    setRoomJoinedData: (state, action: PayloadAction<JoinedRoomData>) => {
      const newRoomsJoined = [...state.roomsJoined];
      const foundIndex = newRoomsJoined.findIndex(
        (d) => d.roomId === action.payload.roomId
      );
      if (foundIndex >= 0) {
        newRoomsJoined.splice(foundIndex, 1, action.payload);
      } else {
        newRoomsJoined.push(action.payload);
      }
      newRoomsJoined.sort(sortByLastChangeDesc);
      return {
        ...state,
        roomsJoined: newRoomsJoined
      };
    },
    removeRoomJoinedData: (state, action: PayloadAction<string>) => {
      const foundIndex = state.roomsJoined.findIndex(
        (d) => d.roomId === action.payload
      );
      if (foundIndex >= 0) {
        const newRoomsJoined = [...state.roomsJoined];
        newRoomsJoined.splice(foundIndex, 1);
        return {
          ...state,
          roomsJoined: newRoomsJoined.sort(sortByLastChangeDesc)
        };
      }
      return state;
    },
    resetRoomsJoinedData: (state, action: PayloadAction<JoinedRoomData[]>) => {
      return {
        ...state,
        roomsJoined: action.payload.sort(sortByLastChangeDesc)
      };
    },
    purgeRoomJoinedData: () => {
      return initialState;
    },
    setDirectChatData: (
      state,
      action: PayloadAction<Record<string, string[]>>
    ) => {
      state.directChatData = action.payload;
    }
  }
});

export const selectRoomIdByDocumentId = (
  state: RootState,
  documentId: string
) => {
  const room = state.matrixRooms.roomsJoined.find(
    (room) => room.documentId === documentId
  );

  return room ? room.roomId : null;
};

export const {
  setRoomJoinedData,
  removeRoomJoinedData,
  resetRoomsJoinedData,
  setDirectChatData,
  purgeRoomJoinedData
} = roomDataSlice.actions;

export default persistReducer(roomDataPersistConfig, roomDataSlice.reducer);
